/**
 * Save serialize/deserialize — pure conversion between in-memory state and JSON.
 *
 * # The two-phase model
 *
 * Saving a game proceeds in two phases:
 *
 *   Phase 1 (serialize): in-memory state -> JS object that is JSON-safe but
 *     not yet stringified. The object is structurally complete except for
 *     the `checksum` field, which is added by integrity.ts.
 *
 *   Phase 2 (envelopeToJson): the now-complete envelope (with checksum) is
 *     canonicalized (keys sorted recursively) and JSON.stringified.
 *
 * Why split? Because the checksum has to be computed over a stable byte
 * sequence. If we stringify before computing checksum, then the checksum
 * field has to be appended somehow — but where? Insertion order differs
 * between v8/spidermonkey/javascriptcore. Different runtimes producing
 * different JSON breaks cross-browser save portability.
 *
 * The fix: compute checksum over the canonicalized JS object (which has a
 * defined ordering by sorted keys), then attach the checksum, then
 * canonicalize-and-stringify the whole envelope once. The output bytes are
 * deterministic regardless of how the envelope was assembled.
 *
 * # Loading is unified
 *
 * `deserialize(json)`:
 *   1. JSON.parse the string. Catch parse errors -> DeserializeError.
 *   2. Extract gameState.version. If missing or non-integer, DeserializeError.
 *   3. Run migrations to bring it up to current SAVE_VERSION.
 *   4. Validate the migrated payload against SaveEnvelopeSchema.
 *   5. Return the validated SaveEnvelope.
 *
 * Checksum verification is NOT done here. integrity.ts handles that.
 * This separation keeps serialize testable without HMAC dependencies and
 * lets Phase B compose: parse first, verify integrity, then trust the data.
 *
 * # RNG state
 *
 * SeededRng has a `serialize()` method that returns `{ s0: string, s1: string }`
 * (bigints stringified for JSON). Round-trip via `SeededRng.deserialize()`
 * is bit-exact: the reconstructed RNG produces identical `nextU64()` output
 * to the original. This file does nothing special with RNG — it just lets
 * the existing serialize/deserialize methods on SeededRng do their work,
 * and validates the snapshot shape via RngSnapshotSchema.
 *
 * # Currency seam
 *
 * Currency values currently serialize as raw numbers (Path C). The schema's
 * CurrencyValueSchema validates `z.number().finite()`. When break_eternity
 * lands, this file gains a per-currency-field map that calls
 * `serializeCurrency` from ../currency.ts. For Phase A there are no currency
 * fields in GameState yet, so this is a forward-compatible no-op.
 *
 * @see {@link ./schema.ts} — SaveEnvelope shape
 * @see {@link ./migrations.ts} — version chain runner
 * @see {@link ./integrity.ts} — checksum computation/verification
 * @see {@link ../rng.ts} — SeededRng serialize/deserialize
 */

import {
  SaveEnvelopeSchema,
  SaveEnvelopeForChecksumSchema,
  ENVELOPE_VERSION,
  type SaveEnvelope,
  type SaveEnvelopeForChecksum,
  type LocalState,
} from './schema';
import { migrate } from './migrations';
import { SAVE_VERSION } from '../state';

// ─────────────────────────────────────────────────────────────────────────────
// Errors
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Thrown by `envelopeToJson` when a JS object can't be safely converted
 * to a save string. Causes: circular references, BigInt without conversion,
 * or schema validation failing on what was supposed to be a complete
 * envelope.
 */
export class SerializeError extends Error {
  public override readonly cause: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'SerializeError';
    this.cause = cause;
  }
}

/**
 * Thrown by `deserialize` when a string can't be loaded as a SaveEnvelope.
 * Causes: invalid JSON, missing version field, migration failure, schema
 * validation failure.
 *
 * Phase B catches this and maps to `SaveLoadFailureReason: 'corrupt'`.
 */
export class DeserializeError extends Error {
  public override readonly cause: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'DeserializeError';
    this.cause = cause;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Canonical key ordering
//
// Recursively sorts object keys alphabetically so that two structurally-equal
// objects produce identical JSON.stringify output regardless of how their keys
// were inserted in memory.
//
// Arrays preserve order (semantic order matters). Objects sort keys.
// Primitives and null are returned as-is.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Recursively sort object keys for stable JSON output.
 *
 * **Important:** does not handle BigInt, Date, RegExp, Function, Symbol,
 * undefined, Map, Set, or class instances. Inputs to canonicalize() must
 * already be JSON-safe (the SaveEnvelope schema enforces this).
 *
 * Cycles in the input throw a RangeError eventually (the recursion never
 * terminates). The schema's `.strict()` validation prevents cyclic structures
 * from being valid envelopes in the first place.
 */
export function canonicalize(value: unknown): unknown {
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((v) => canonicalize(v));
  }
  // Object — sort keys
  const sorted: Record<string, unknown> = {};
  const keys = Object.keys(value as Record<string, unknown>).sort();
  for (const k of keys) {
    sorted[k] = canonicalize((value as Record<string, unknown>)[k]);
  }
  return sorted;
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 1: build a partial envelope (no checksum yet)
//
// integrity.ts will compute checksum over this and produce a complete
// SaveEnvelope.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build the pre-checksum portion of a save envelope from in-memory state.
 *
 * Pure function. No I/O, no time-based randomness. The `createdAt` and
 * `installId` parameters are passed in by the caller (Phase B's adapter)
 * because:
 *
 *   - `createdAt`: reading the clock is impure; passing it explicitly keeps
 *     this function testable (tests inject a fixed timestamp)
 *   - `installId`: read once at boot from IndexedDB by Phase B's adapter
 *
 * # Validation
 *
 * The returned object is validated against `SaveEnvelopeForChecksumSchema`
 * before return. Any state shape that doesn't match the current schema
 * causes a SerializeError. This is intentional fail-fast: serializing a
 * malformed state would produce a corrupt save, and we'd rather crash
 * during write than during a future load.
 */
export function buildEnvelopeForChecksum(
  state: LocalState,
  installId: string,
  createdAt: number,
): SaveEnvelopeForChecksum {
  const candidate: SaveEnvelopeForChecksum = {
    envelopeVersion: ENVELOPE_VERSION,
    createdAt,
    installId,
    state,
  };

  const result = SaveEnvelopeForChecksumSchema.safeParse(candidate);
  if (!result.success) {
    throw new SerializeError(
      `state failed schema validation: ${result.error.message}`,
      result.error,
    );
  }
  return result.data;
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 2: turn a complete envelope into JSON
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Serialize a complete SaveEnvelope (including checksum) to a JSON string
 * suitable for IndexedDB storage.
 *
 * Output is canonicalized (keys recursively sorted) so that the same envelope
 * always produces the same bytes — important for cross-runtime save
 * portability and for HMAC consistency on round-trip verification.
 *
 * Caller is expected to have already attached a valid checksum via
 * integrity.attachChecksum. Validates against SaveEnvelopeSchema before
 * stringifying; throws SerializeError on schema failure.
 */
export function envelopeToJson(envelope: SaveEnvelope): string {
  const result = SaveEnvelopeSchema.safeParse(envelope);
  if (!result.success) {
    throw new SerializeError(
      `complete envelope failed schema validation: ${result.error.message}`,
      result.error,
    );
  }
  const canonical = canonicalize(result.data);
  try {
    return JSON.stringify(canonical);
  } catch (err) {
    throw new SerializeError(
      `JSON.stringify failed (likely circular reference or BigInt)`,
      err,
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Deserialize: JSON string -> SaveEnvelope
//
// Loads, migrates, validates. Does NOT verify checksum (integrity.ts does
// that). Returns a typed SaveEnvelope ready for further integrity checks.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parse and validate a save string. Runs migrations to current SAVE_VERSION.
 *
 * Sequence:
 *   1. JSON.parse -> raw object (or DeserializeError on bad JSON)
 *   2. Extract gameState.version (or DeserializeError if missing/invalid)
 *   3. migrate() to current version (DeserializeError wraps any MigrationError)
 *   4. SaveEnvelopeSchema.parse() (DeserializeError wraps validation failure)
 *
 * # Why we extract version before migrating
 *
 * The migration system needs to know which version to start chain from.
 * That version is embedded in the save itself, but at v1 it lives at
 * `state.gameState.version`. Future envelope schema changes might move it.
 * This function knows where it lives in the wire format and extracts it
 * defensively.
 *
 * # What we do NOT do here
 *
 *   - Verify checksum (integrity.ts)
 *   - Reconstruct SeededRng (caller does this with rng field after deserialize)
 *   - Apply state to gameStore (Phase B adapter does this)
 */
export function deserialize(json: string): SaveEnvelope {
  // Step 1: parse
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (err) {
    throw new DeserializeError(
      `invalid JSON: ${err instanceof Error ? err.message : String(err)}`,
      err,
    );
  }

  // Step 2: extract version
  const version = extractVersion(parsed);

  // Step 3: migrate (no-op when version === SAVE_VERSION)
  let migrated: unknown;
  try {
    migrated = migrate(parsed, version);
  } catch (err) {
    throw new DeserializeError(
      `migration failed: ${err instanceof Error ? err.message : String(err)}`,
      err,
    );
  }

  // Step 4: validate
  const result = SaveEnvelopeSchema.safeParse(migrated);
  if (!result.success) {
    throw new DeserializeError(
      `envelope schema validation failed: ${result.error.message}`,
      result.error,
    );
  }

  return result.data;
}

/**
 * Extract gameState.version from a parsed save payload. Defensive — handles
 * missing/malformed input by throwing DeserializeError with helpful context.
 */
function extractVersion(payload: unknown): number {
  if (payload === null || typeof payload !== 'object') {
    throw new DeserializeError(
      `payload must be an object, got ${payload === null ? 'null' : typeof payload}`,
    );
  }
  const root = payload as Record<string, unknown>;
  const state = root['state'];
  if (state === null || typeof state !== 'object') {
    throw new DeserializeError(`payload.state missing or not an object`);
  }
  const gameState = (state as Record<string, unknown>)['gameState'];
  if (gameState === null || typeof gameState !== 'object') {
    throw new DeserializeError(`payload.state.gameState missing or not an object`);
  }
  const version = (gameState as Record<string, unknown>)['version'];
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) {
    throw new DeserializeError(
      `payload.state.gameState.version must be a positive integer, got ${typeof version}: ${String(version)}`,
    );
  }
  return version;
}

// ─────────────────────────────────────────────────────────────────────────────
// Re-exports for ergonomics
// ─────────────────────────────────────────────────────────────────────────────

export { SAVE_VERSION, ENVELOPE_VERSION };
